import React from "react";

import { File, Globe, Pencil, Trash2, Users } from "lucide-react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/src/components/ui/avatar";

import { getInitials } from "../../common/AvatarStack";
import DefaultAvatarImage from "../../common/DefaultAvatarImage";
import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  countColumn,
  dateColumn,
} from "../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../dataTable/DataTableLinkCell";
import { castingPath } from "../../routes/routes";

import type { TFunction } from "i18next";

import type { CastingListItem } from "../../../api/MediaAPI.types";
import type {
  ColumnLayout,
  DataTableColumnDef,
} from "../../dataTable/dataTable.types";

interface CastingColumnsOptions {
  t: TFunction;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string) => void;
}

const LANGUAGES_LAYOUT: ColumnLayout = { width: "11%", minWidth: 120 };
const COUNT_LAYOUT: ColumnLayout = { width: "11%" };

export function castingColumns({
  t,
  onEdit,
  onDelete,
}: CastingColumnsOptions): DataTableColumnDef<CastingListItem>[] {
  return [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("title")} />
      ),
      meta: { searchable: true },
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <Avatar size="sm">
            <AvatarImage
              alt=""
              className="object-cover"
              src={row.original.cover_picture_1_1 ?? undefined}
            />
            <AvatarFallback className="text-xs">
              {getInitials(row.original.name) || <DefaultAvatarImage />}
            </AvatarFallback>
          </Avatar>
          <DataTableLinkCell href={castingPath(row.original.uuid)}>
            {row.original.name || t("newCasting")}
          </DataTableLinkCell>
        </div>
      ),
    },
    countColumn<CastingListItem>(
      "languages_count",
      t("languages"),
      <Globe />,
      LANGUAGES_LAYOUT,
    ),
    countColumn<CastingListItem>(
      "medias_count",
      t("contents_count"),
      <File />,
      COUNT_LAYOUT,
    ),
    countColumn<CastingListItem>(
      "smartlists_count",
      t("curators"),
      <Users />,
      COUNT_LAYOUT,
    ),
    dateColumn<CastingListItem>("updated_at", t("update_date")),
    actionsColumn<CastingListItem>(
      (casting) => [
        {
          label: t("edit"),
          icon: <Pencil />,
          onClick: () => onEdit(casting.uuid),
        },
        {
          label: t("delete"),
          icon: <Trash2 />,
          variant: "destructive",
          onClick: () => onDelete(casting.uuid),
          confirm: {
            title: t("deleteTitle"),
            description: t("deleteBody", { casting: casting.name }),
            confirmLabel: t("modal:submit"),
          },
        },
      ],
      (casting) => casting.name || t("newCasting"),
    ),
  ];
}
